/**
 * Groups `analysis_layers.key` values for **Admin → Role default layers**.
 *
 * **Order:** The first sections use `LAYER_TOGGLE_PANEL_TAB_ORDER` from
 * `analysisLayerEntitlements.ts` — the same sequence as the vertical/horizontal tabs in
 * `LayerTogglePanel` (large-scale Analysis panel in `AnalysisPanel`). Then **Digisaka GPT**
 * (`mapChat`), **Farm-level Analysis Tools** (`analysisPanel` — heat map, time series,
 * calendar, climate, field report), then any DB keys not listed in `GROUP_FOR_KEY`
 * (`uncategorized`).
 */
import {
  LAYER_TOGGLE_PANEL_TAB_ORDER,
  type LayerTogglePanelTabId,
} from './analysisLayerEntitlements';

/**
 * Entitlements that drive **farm drawer** Analysis Tools (see `AnalysisPanel`).
 * Rice forecast keys are listed here for gating the field rice tab; **Admin → Role default layers**
 * groups them under **Rice Yield Forecast** above.
 */
export const FARM_LEVEL_ANALYSIS_TOOL_KEYS = [
  'heatMap',
  'timeSeries',
  'cropCalendar',
  'climate',
  'fieldReport',
  'field_report',
  'farmRiceYieldForecast',
  'farm_rice_yield_forecast',
  'multipleFieldAnalysis',
  'multiple_field_analysis',
  'soilMoisture',
  'soil_moisture',
  'blsForecast',
  'bls_forecast',
] as const;

export type AdminRoleLayerUiGroupId =
  | LayerTogglePanelTabId
  | 'mapChat'
  | 'analysisPanel'
  | 'uncategorized';

export const ADMIN_ROLE_LAYER_UI_GROUP_ORDER: readonly AdminRoleLayerUiGroupId[] = [
  ...LAYER_TOGGLE_PANEL_TAB_ORDER,
  'mapChat',
  'analysisPanel',
  'uncategorized',
];

export const ADMIN_ROLE_LAYER_UI_GROUP_LABEL: Record<AdminRoleLayerUiGroupId, string> = {
  forecast: 'Rice Yield Forecast',
  multiTemporal: 'Multi-Temporal Maps',
  droneData: 'Sample Data',
  fieldServices: 'Field services',
  cropArea: 'Crop Type Maps',
  disasterLossDamage: 'Disaster Loss & Damages',
  lowCarbon: 'Low Carbon Rice',
  treeDetection: 'AI Detection: Trees, Crowns & Fields',
  mapChat: 'Pandoy AI (map chat)',
  analysisPanel: 'Farm-level analysis (Analysis Tools)',
  uncategorized: 'Uncategorized layer keys',
};

/** Short hint under the section header (map vs field context). */
export const ADMIN_ROLE_LAYER_UI_GROUP_HINT: Partial<Record<AdminRoleLayerUiGroupId, string>> = {
  forecast:
    'Regional MODIS rice forecast (`riceForecast`) only. Farm POST (`farmRiceYieldForecast`) is a separate layer under Farm-level analysis.',
  multiTemporal:
    'CADI, pest, land cover, LST, planting method, rice growth stage. Farm Index Map / Time Series do not unlock this nav button.',
  droneData:
    'Isabela / Cavite COG cards, then Leads Agri drone sites by province (`droneSites` — Nueva Ecija, Batangas, Laguna, Quezon). Users with any drone key also see `droneSites`.',
  fieldServices:
    'Drone mapping, drone spraying, CH4 & AWD sensors, and soil sensors. Default audience tag is carbon only.',
  cropArea: 'Maize, Sugarcane and Rice. Hidden if none of those keys are assigned.',
  disasterLossDamage: 'Flood and loss explorer. Hidden if none of those keys are assigned.',
  lowCarbon: 'Large-scale panel — straw burn / low-carbon rice.',
  treeDetection: 'AI detection — tree, crown, and field boundary inference tools.',
  mapChat: 'Map widget — Kimi agent chat; not a map layer checkbox.',
  analysisPanel:
    'Field drawer — Index Map, Time Series, Crop Calendar, Climate (`/api/climate-data/`), Field report (`/api/fieldreport/`), farm rice yield POST (`/api/farm-rice-yield-forecast/`), multiple-field analysis (`/api/multiple-field-analysis/`), banana BLS forecast (`/api/bls-forecast`).',
  uncategorized: 'Keys missing from `GROUP_FOR_KEY` in this file — add a mapping so they appear in the right card.',
};

/**
 * Row order inside each admin card — mirrors `LayerTogglePanel` / Analysis Tools where applicable.
 * Keys omitted from a list are sorted by name after the listed keys.
 */
const ADMIN_GROUP_ROW_ORDER: Partial<Record<AdminRoleLayerUiGroupId, readonly string[]>> = {
  forecast: ['riceForecast', 'rice_yield_forecast'],
  // Multi-Temporal tab: CADI (featured), then LST, VHI, BSRI, planting (2025 before 2024).
  multiTemporal: [
    'cadiDrought',
    'pestRisk',
    'lst',
    'vhi',
    'bsri',
    'plantingMethodDry2025',
    'plantingMethodDry2024',
    'riceGrowthStage',
    'irrigationType',
  ],
  droneData: [
    'isabelaPhilriceNDVI',
    'isabelaPhilriceOrtho',
    'caviteSilangNDVI',
    'caviteSilangOrtho',
    'droneSites',
  ],
  fieldServices: ['fieldServices'],
  cropArea: ['maize', 'sugarCane', 'rice', 'coconut'],
  disasterLossDamage: ['floodDamage', 'disasterLossDamage', 'flood'],
  lowCarbon: ['strawBurn', 'lowCarbon'],
  treeDetection: ['treeDetection', 'crownSegmentation', 'cropfieldDelineation'],
  mapChat: ['digisakaGpt', 'digisaka_gpt'],
  // Matches Analysis Tools strip: Heat → Time series → Crop calendar → Climate → Report (rice forecast → Rice Yield Forecast card).
  analysisPanel: [
    'heatMap',
    'soilMoisture',
    'soil_moisture',
    'timeSeries',
    'cropCalendar',
    'climate',
    'fieldReport',
    'farmRiceYieldForecast',
    'multipleFieldAnalysis',
    'blsForecast',
    'bls_forecast',
  ],
};

export function sortRoleLayerRowsForGroup<T extends { key: string; name: string }>(
  groupId: AdminRoleLayerUiGroupId,
  rows: T[]
): T[] {
  const order = ADMIN_GROUP_ROW_ORDER[groupId];
  if (!order?.length) {
    return [...rows].sort((a, b) => a.name.localeCompare(b.name));
  }
  const rank = new Map<string, number>();
  order.forEach((k, i) => rank.set(k, i));
  return [...rows].sort((a, b) => {
    const ra = rank.get(a.key);
    const rb = rank.get(b.key);
    if (ra !== undefined && rb !== undefined && ra !== rb) return ra - rb;
    if (ra !== undefined && rb === undefined) return -1;
    if (ra === undefined && rb !== undefined) return 1;
    return a.name.localeCompare(b.name);
  });
}

const GROUP_FOR_KEY: Record<string, AdminRoleLayerUiGroupId> = {
  riceForecast: 'forecast',
  rice_yield_forecast: 'forecast',

  bsri: 'multiTemporal',
  vhi: 'multiTemporal',
  cadiDrought: 'multiTemporal',
  pestRisk: 'multiTemporal',
  pest_risk: 'multiTemporal',
  lst: 'multiTemporal',
  heatMap: 'analysisPanel',
  /** M2IMOSA soil moisture heatmap (`/api/soil-moisture/`) — farm heat-map flow */
  soilMoisture: 'analysisPanel',
  soil_moisture: 'analysisPanel',
  timeSeries: 'analysisPanel',
  plantingMethodDry2025: 'multiTemporal',
  plantingMethodDry2024: 'multiTemporal',
  riceGrowthStage: 'multiTemporal',
  irrigationType: 'multiTemporal',

  isabelaPhilriceNDVI: 'droneData',
  isabelaPhilriceOrtho: 'droneData',
  caviteSilangNDVI: 'droneData',
  caviteSilangOrtho: 'droneData',
  droneSites: 'droneData',
  fieldServices: 'fieldServices',

  maize: 'cropArea',
  sugarCane: 'cropArea',
  sugarcane: 'cropArea',
  rice: 'cropArea',
  rice_area: 'cropArea',
  coconut: 'cropArea',

  floodDamage: 'disasterLossDamage',
  disasterLossDamage: 'disasterLossDamage',
  flood: 'disasterLossDamage',

  strawBurn: 'lowCarbon',
  lowCarbon: 'lowCarbon',

  treeDetection: 'treeDetection',
  tree_detection: 'treeDetection',
  crownSegmentation: 'treeDetection',
  /** Legacy / alternate DB spellings (snake_case, etc.) */
  crown_segmentation: 'treeDetection',
  cropfieldDelineation: 'treeDetection',
  cropfield_delineation: 'treeDetection',

  cropCalendar: 'analysisPanel',
  climate: 'analysisPanel',
  fieldReport: 'analysisPanel',
  field_report: 'analysisPanel',
  farmRiceYieldForecast: 'analysisPanel',
  farm_rice_yield_forecast: 'analysisPanel',
  multipleFieldAnalysis: 'analysisPanel',
  multiple_field_analysis: 'analysisPanel',
  blsForecast: 'analysisPanel',
  bls_forecast: 'analysisPanel',
  digisakaGpt: 'mapChat',
  digisaka_gpt: 'mapChat',
};

/** Case-insensitive fallback so e.g. `Treedetection` maps like `treeDetection`. */
const GROUP_FOR_KEY_INSENSITIVE = new Map<string, AdminRoleLayerUiGroupId>();
for (const [mapKey, groupId] of Object.entries(GROUP_FOR_KEY)) {
  GROUP_FOR_KEY_INSENSITIVE.set(mapKey.toLowerCase(), groupId);
}

export function adminRoleLayerUiGroupForKey(key: string): AdminRoleLayerUiGroupId {
  const k = (key || '').trim();
  if (!k) return 'uncategorized';
  return GROUP_FOR_KEY[k] ?? GROUP_FOR_KEY_INSENSITIVE.get(k.toLowerCase()) ?? 'uncategorized';
}
